<template>
  <div>
    <div class="card rounded-top-0 rounded-bottom-4 border-0 shadow">
      <div
          class="confirmation-card-body card-body text-center py-3 px-sm-4 py-sm-4 screen-content"
      >
        <error-list></error-list>

        <div
            v-if="booking"
            class="confirmation"
        >
          <booking-summary
              :event="booking.period.event"
              :booking="booking"
              :errors="[]"
              :hide-errors="true"
              class="mb-3"
          >
          </booking-summary>

          <hr/>

          <div v-if="loadingStatus" class="alert alert-warning d-flex align-items-center justify-content-center mb-3">
            <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
            <strong v-if="inPosMode()">{{ $t('followInstructionsPayTerminal') }}</strong>
            <strong v-else>{{ $t('loading') }}</strong>
          </div>

          <div v-if="!loadingStatus">
            <div v-if="booking.status  === 'open'">
              <p class="card-text">
                {{ $t('confirmation.open') }}
              </p>

              <p v-if="booking.period.assign_started">
                {{ $t('confirmation.openAssignStarted') }}
              </p>

              <p v-else class="card-text">
                <i18n path="confirmation.openAssignNotStarted">
                  <em slot="assign">{{ booking.period.assign }}</em>
                </i18n>
              </p>
            </div>

            <div v-if="booking.status  === 'paid'" class="pt-3">
              <h3 class="d-flex align-items-center justify-content-center text-success mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor"
                     class="bi bi-check-circle-fill" viewBox="0 0 16 16">
                  <path
                      d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                </svg>
                <span class="ms-2">{{ $t('confirmation.thanks') }}</span>
              </h3>

              <template v-if="inPosMode()">
                <p class="text-start">
                  {{ $t('confirmation.printingTicket') }}
                </p>
              </template>

              <template v-else>
                <p
                    v-if="hasAllLockers"
                    class="text-start"
                >
                  <template v-if="booking.has_electronic_lockers">
                    {{ $t('confirmation.paidHasElectronicLockers') }}
                  </template>
                  <template v-else>
                    {{ $t('confirmation.paidHasLockers') }}
                  </template>
                  {{ booking.email }}
                </p>

                <p v-else-if="booking.period.assign_started" class="text-start">
                  <template v-if="booking.has_electronic_lockers">
                    {{ $t('confirmation.paidAssignStartedElectronic') }}
                  </template>
                  <template v-else>
                    {{ $t('confirmation.paidAssignStarted') }}
                  </template>
                </p>

                <p v-else class="text-start">
                  <template v-if="booking.has_electronic_lockers">
                    <i18n path="confirmation.paidAssignNotStartedElectronic">
                      <em slot="assign">{{ booking.period.assign }}</em>
                    </i18n>
                  </template>
                  <template v-else>
                    <i18n path="confirmation.paidAssignNotStarted">
                      <em slot="assign">{{ booking.period.assign }}</em>
                    </i18n>
                  </template>
                </p>
              </template>
            </div>

            <div class="alert alert-danger" v-if="booking.status === 'canceled'">
              <h2 class="h4">
                {{ $t('confirmation.canceled') }}
              </h2>

              <p class="mb-0">
                {{ $t('confirmation.canceledText') }}
              </p>
            </div>

            <div class="locker-code-container-wrapper" v-if="!inPosMode()">
              <locker-code
                  v-for="locker in lockers"
                  v-bind:key="locker.locker_number"
                  :lockerSize="locker.size"
                  :locker="locker"
                  :token="token"
                  :periodLocker="token"
              ></locker-code>
            </div>
          </div>

          <div class="d-flex pt-4">
            <router-link
                v-if="!loadingStatus"
                :to="'/event/' + booking.period.event.code"
                class="btn btn-success flex-fill"
            >
              {{ inPosMode() ? $t('backToHomeScreen') : $t('rentAnotherLocker') }}
            </router-link>
          </div>
        </div>

        <div class="d-flex pt-4" v-if="bookingError">
          <button
              class="btn btn-success flex-fill"
              @click="reload"
          >
            {{ $t('refresh') }}
          </button>
        </div>
      </div>

      <div
          v-if="booking && hasAllLockers && inPosMode()"
          class="print-content"
      >
        <ticket
            v-if="activeTicket"
            v-bind:key="activeTicket.locker_number"
            :lockerSize="activeTicket.size"
            :locker="activeTicket"
            :period="booking.period"
            :event="booking.period.event"
            @loaded="ticketLoaded"
        ></ticket>
      </div>
    </div>
  </div>
</template>

<script>
import BookingSummary from '../components/BookingSummary';
import LockerCode from '../components/LockerCode';
import Ticket from '../components/Ticket';
import eventBus from '../eventBus';
import BookingService from '../services/BookingService';
import posMixin from '../mixins/posMixin';
import EventBus from '@/eventBus';
import Booking from '@/models/Booking';

const reloadTimoutPortal = 5; // Reload timeout for the portal in seconds
const reloadTimoutPos = 1; // Reload timeout for the pos in seconds

export default {
  components: {
    LockerCode,
    BookingSummary,
    Ticket
  },

  mixins: [posMixin],

  props: [
    'token'
  ],

  data() {
    return {
      booking: null,
      bookingError: false,
      loadingStatus: true,
      waitingForStatus: false,
      printedTickets: 0
    }
  },

  computed: {
    /**
     * Get a list of all lockers.
     *
     * @return {Array}
     */
    lockers() {
      let lockers = [];

      for (let line of this.booking.lines) {
        for (let locker of line.lockers) {
          locker.size = line.name;

          lockers.push(locker);
        }
      }

      return lockers;
    },

    /**
     * Get the active ticket to print.
     *
     * @return {Object}
     */
    activeTicket() {
      return this.lockers[this.printedTickets];
    },

    /**
     * Is reload of booking needed?
     * - Status is open
     * - Status is paid and we're waiting for lockers
     */
    reloadBookingNeeded() {
      if (this.waitingForStatus && !this.inPosMode()) {
        return false;
      }

      return this.booking.status === 'open' || (this.booking.status === 'paid' && this.waitingForLockers);
    },

    /**
     * Are we waiting for lockers?
     *
     * @return Boolean
     */
    waitingForLockers() {
      return this.booking.period.assign_started && !this.hasAllLockers;
    },

    /**
     * Get number of booked lockers.
     *
     * @return Number
     */
    numberOfBookedLockers() {
      let number = 0;

      for (let line of this.booking.lines) {
        number += line.quantity;
      }

      return number;
    },

    /**
     * Get number of assigned lockers.
     *
     * @return Number
     */
    numberOfAssignedLockers() {
      let number = 0;

      for (let line of this.booking.lines) {
        number += line.lockers.length;
      }

      return number;
    },

    /**
     * Has this booking assigned all lockers?
     *
     * @return Boolean
     */
    hasAllLockers() {
      return this.numberOfAssignedLockers === this.numberOfBookedLockers;
    },

    /**
     * Timeout in seconds for the current reload.
     *
     * @return Number
     */
    reloadTimout() {
      return this.inPosMode() ? reloadTimoutPos : reloadTimoutPortal;
    }
  },

  watch: {
    // Call again the method to get the event if the route changes
    '$route': 'init',

    /**
     * Check if all tickets are printed and redirect.
     *
     * @param {Number} val
     */
    printedTickets(val) {
      if (val === this.numberOfAssignedLockers) {
        this.redirect();
      }
    }
  },

  mounted() {
    let that = this;
    
    this.$nextTick(() => {
      window.scrollTo(0, 0);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 50);
    });

    /**
     * Increase printed tickets.
     */
    window.onafterprint = function () {
      that.printedTickets++;
    }
  },

  created() {
    this.init();
  },

  methods: {
    /**
     * Initialize booking confirmation component.
     */
    init() {
      this.getBooking();
    },

    /**
     * Get the requested booking.
     *
     * @return Promise
     */
    getBooking() {
      this.loadingStatus = true;

      return BookingService.get(this.token).then(response => {
        if (!response.data) {
          return;
        }

        this.booking = Object.assign(new Booking(), response.data);

        // Update event for background
        EventBus.$emit('setEvent', this.booking.period.event);

        // If reload booking is needed, retrieve status/lockers again after x seconds
        if (this.reloadBookingNeeded) {
          this.reloadBooking();
          return;
        }

        this.waitingForStatus = false;
        this.loadingStatus = false;
      }).catch(error => {
        this.bookingError = true;

        if (error.response.status !== 404) {
          return;
        }

        eventBus.$emit('error', this.$t('bookingNotFound'));
      });
    },

    /**
     * Reload booking.
     */
    reloadBooking() {
      this.waitingForStatus = true;

      setTimeout(function () {
        this.getBooking()
      }.bind(this), this.reloadTimout * 1000);
    },

    /**
     * Called after a new ticket has been loaded.
     */
    ticketLoaded() {
      setTimeout(function () {
        window.print();
      }, 100);
    },

    /**
     * Redirect back to home.
     */
    redirect() {
      this.$router.push({
        'name': 'home'
      });
    },

    /**
     * Reload whole page.
     */
    reload() {
      window.location.reload();
    }
  }
}
</script>
